import {
  Badge,
  Dropdown,
  Flex,
  type MenuProps,
  Table,
  type TableProps,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { generatePath, Link, useMatch, useParams } from 'react-router-dom';

import IconArrowRight from '@/assets/icons/arrow-right.svg?react';
import IconMoreHoriz from '@/assets/icons/more-horiz.svg?react';
import { Tag } from '@/components';
import { DATE_FORMAT_JP, TIME_ZONE } from '@/constants/datetime.constants';
import { ROUTES_PATH } from '@/constants/routes.constants';
import {
  DEFAULT_DROPDOWN_OPTIONS,
  DEFAULT_PAGINATION_OPTIONS,
} from '@/constants/table.constants';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useUserInformation } from '@/hooks/use-user-information';

import {
  SHOP_CONTRACT_TYPES_TEXTS,
  SHOP_REGISTRATION_STATUS_CONTENTS,
} from '../../constants';
import { useDeleteShop } from '../../mutations';
import {
  ShopContractStatusEnum,
  ShopRegistrationStatusEnum,
  type ShopResponseItem,
} from '../../types';

const { Text } = Typography;

function getRoutePath(
  isShopList: boolean,
  isWeddingStore: boolean,
  id: string | undefined,
): string {
  if (isShopList) {
    return isWeddingStore
      ? ROUTES_PATH.shopWeddingsDetail
      : ROUTES_PATH.shopDetail;
  }

  if (id) {
    return isWeddingStore
      ? ROUTES_PATH.organizationShopsWeddingDetail
      : ROUTES_PATH.organizationShopsDetail;
  }

  return isWeddingStore
    ? ROUTES_PATH.myOrganizationShopsWeddingsDetail
    : ROUTES_PATH.myOrganizationShopsDetail;
}

interface ShopTableProps extends TableProps<ShopResponseItem> {
  shopNameTitle: string;
  isShopList?: boolean;
  isWeddingChain?: boolean;
  contractType?: ShopResponseItem['contract_type'];
  deleteShop: ReturnType<typeof useDeleteShop>['mutate'];
}

export function ShopTable({
  shopNameTitle,
  isShopList = false,
  contractType,
  isWeddingChain,
  deleteShop,
  ...rest
}: Readonly<ShopTableProps>) {
  const { id } = useParams();
  const { confirm } = useModalMethod();
  const { isChainRole, isSbfMasterOrSbsOperationRole } = useUserInformation();
  const isShopListRoute = useMatch(ROUTES_PATH.shopList);

  async function handleDeleteShop(shopId: number, confirmTitle: string) {
    const confirmed = await confirm({
      title: `${confirmTitle}の削除`,
      content: `この${confirmTitle}を削除しますか？`,
      okText: '削除',
    });
    if (!confirmed) return;
    deleteShop(shopId);
  }

  const items = (id: number, isWeddingStore: boolean): MenuProps['items'] => {
    const itemLabel = isShopListRoute
      ? shopNameTitle
      : isWeddingStore
        ? '企画'
        : '店舗';
    return [
      {
        key: id,
        label: (
          <Text>
            {`${itemLabel}の削除`}
            <IconArrowRight className="ml-24" />
          </Text>
        ),
        onClick: () => void handleDeleteShop(id, itemLabel),
      },
    ];
  };

  const columns: ColumnsType<ShopResponseItem> = [
    {
      title: `${shopNameTitle}名`,
      dataIndex: 'name',
      ellipsis: true,
      render: (
        shopName,
        {
          id: shopId,
          contract_status: contractStatus,
          is_wedding_store: isWeddingStore,
          is_printer_and_fixture_ordered: isPrinterAndFixtureOrdered,
        },
      ) => (
        <Link
          to={generatePath(getRoutePath(isShopList, isWeddingStore, id), {
            id: id! ?? shopId,
            shopId: String(shopId),
          })}
        >
          <Flex align="center" justify="space-between">
            <Text underline ellipsis className="white-space-pre">
              {shopName}
            </Text>
            {!isChainRole &&
              !isWeddingStore &&
              !isPrinterAndFixtureOrdered &&
              contractStatus === ShopContractStatusEnum['in-use'] && (
                <Tag color="processing" text="什器なし" />
              )}
          </Flex>
        </Link>
      ),
    },
    ...(isShopList
      ? [
          {
            title: 'チェーン名',
            dataIndex: 'organization_name',
            className: 'white-space-pre',
            width: '21%',
            ellipsis: true,
          },
        ]
      : []),
    ...(isWeddingChain
      ? []
      : [
          {
            title: '契約種類',
            dataIndex: 'contract_type',
            width: '13%',
            ellipsis: true,
            render: (
              shopContractType: ShopResponseItem['contract_type'],
              { is_wedding_store: isWeddingStore }: ShopResponseItem,
            ) =>
              isWeddingStore
                ? SHOP_CONTRACT_TYPES_TEXTS.normal
                : (SHOP_CONTRACT_TYPES_TEXTS[
                    contractType ?? shopContractType
                  ] ?? '-'),
          },
        ]),
    {
      title: '利用開始日',
      dataIndex: 'operation_start_date',
      width: '16%',
      ellipsis: true,
      render: operationStartDate =>
        operationStartDate
          ? dayjs(operationStartDate).tz(TIME_ZONE).format(DATE_FORMAT_JP)
          : '-',
    },
    ...(isShopList
      ? []
      : [
          {
            title: '利用終了日',
            dataIndex: 'operation_end_date',
            width: '16%',
            ellipsis: true,
            render: (operationEndDate: string) =>
              operationEndDate
                ? dayjs(operationEndDate).tz(TIME_ZONE).format(DATE_FORMAT_JP)
                : '-',
          },
        ]),
    {
      title: 'ステータス',
      width: '13%',
      ellipsis: true,
      render: ({
        contract_status: contractStatus,
        registration_status: registrationStatus,
      }: ShopResponseItem) => {
        const status =
          ShopRegistrationStatusEnum.official === registrationStatus
            ? contractStatus
            : registrationStatus;

        return <Badge {...SHOP_REGISTRATION_STATUS_CONTENTS[status]} />;
      },
    },
    ...(isSbfMasterOrSbsOperationRole
      ? ([
          {
            width: 60,
            align: 'center',
            className: 'py-0',
            render: (_, { id, is_wedding_store: isWeddingStore }) => (
              <Dropdown
                menu={{ items: items(id, isWeddingStore) }}
                {...DEFAULT_DROPDOWN_OPTIONS}
              >
                <IconMoreHoriz />
              </Dropdown>
            ),
          },
        ] as ColumnsType<ShopResponseItem>)
      : []),
  ];

  return (
    <Table
      rowKey="id"
      bordered
      columns={columns}
      {...rest}
      pagination={{ ...DEFAULT_PAGINATION_OPTIONS, ...rest.pagination }}
    />
  );
}
